import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useSnackbar } from 'notistack';
import { useGetRunsheet, useUpdateRunsheet } from '@/adapters/runsheet-v2';
import { RUNSHEETS_URL } from '@/routePaths';
import RunsheetRowsTable, { EditableRunsheetRow, stripKeys, withKeys } from './RunsheetRowsTable';

const RunsheetEditor: FC = () => {
  const { runsheetId } = useParams<{ runsheetId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { data: runsheet, isLoading, isError } = useGetRunsheet(runsheetId);
  const updateRunsheet = useUpdateRunsheet();

  const [title, setTitle] = useState('');
  const [rows, setRows] = useState<EditableRunsheetRow[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (runsheet) {
      setTitle(runsheet.title);
      setRows(withKeys(runsheet.normalizedRows));
      setIsDirty(false);
    }
  }, [runsheet]);

  const handleSave = async (): Promise<void> => {
    if (!runsheetId || !runsheet) return;
    if (!title.trim()) {
      enqueueSnackbar('Title is required', { variant: 'warning' });
      return;
    }
    const validRows = rows.filter((r) => r.title.trim().length > 0);
    if (validRows.length === 0) {
      enqueueSnackbar('At least one row with a title is required', {
        variant: 'warning',
      });
      return;
    }
    try {
      await updateRunsheet.mutateAsync({
        runsheetId,
        title: title.trim(),
        normalizedRows: stripKeys(validRows),
      });
      enqueueSnackbar('Runsheet saved', { variant: 'success' });
      setIsDirty(false);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to save runsheet', {
        variant: 'error',
      });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={18} />
        <Typography>Loading runsheet…</Typography>
      </Box>
    );
  }

  if (isError || !runsheet) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Runsheet not found or has been deleted.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(RUNSHEETS_URL)}>
          Back to runsheets
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(RUNSHEETS_URL)} size="small">
          Back to runsheets
        </Button>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Edit Runsheet
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Chip label={`Stream key: ${runsheet.streamKey}`} size="small" variant="outlined" />
            <Chip
              label={`Created ${new Date(runsheet.createdAt).toLocaleString()}`}
              size="small"
              variant="outlined"
            />
          </Stack>
        </Box>
        <Button
          variant="contained"
          startIcon={updateRunsheet.isLoading ? <CircularProgress size={16} /> : <SaveIcon />}
          disabled={!isDirty || updateRunsheet.isLoading}
          onClick={handleSave}
        >
          {updateRunsheet.isLoading ? 'Saving…' : 'Save changes'}
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setIsDirty(true);
            }}
            fullWidth
            required
          />
          <RunsheetRowsTable
            rows={rows}
            onChange={(next) => {
              setRows(next);
              setIsDirty(true);
            }}
          />
        </Stack>
      </Paper>
    </Box>
  );
};

export default RunsheetEditor;
